import { apiError } from "../../../../../../utils/formErrors";
import React, { useState } from "react";
import { Box, Stack, Dialog } from "@mui/material";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import { StatusChip } from "../../../../../../component/listing";
import { replyPayslipQuery, resolvePayslipQuery } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../theme";

import InlineMessage from "../../../../../../component/InlineMessage";
const STATUS_TONE = { open: "warning", under_review: "info", resolved: "success", rejected: "danger" };
const STATUS_LABEL = { open: "Open", under_review: "Under review", resolved: "Resolved", rejected: "Rejected" };
const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// A read/reply thread for a payslip query. `isAdmin` shows resolve controls.
const QueryThread = ({ open, query, isAdmin, onClose, onChanged }) => {
  const notify = useNotify();
  const [reply, setReply] = useState("");
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);
  if (!query) return null;
  const closed = ["resolved", "rejected"].includes(query.status);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const res = await replyPayslipQuery(query._id, reply.trim());
      if (res?.success === false) throw apiError(res);
      setReply("");
      onChanged?.(res.data);
    } catch (e) { notify.error(e?.message || "Could not send reply"); }
    finally { setBusy(false); }
  };

  const close = async (status) => {
    setBusy(true);
    try {
      const res = await resolvePayslipQuery(query._id, { status, resolution: resolution.trim() });
      if (res?.success === false) throw apiError(res);
      notify.success(`Query ${status}`);
      onChanged?.(res.data);
    } catch (e) { notify.error(e?.message || "Could not update query"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "36rem", maxWidth: "94vw" } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: "1rem" }}>
        <Box>
          <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>{query.subject}</Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Raised {fmt(query.createdAt)}</Box>
        </Box>
        <StatusChip label={STATUS_LABEL[query.status] || query.status} tone={STATUS_TONE[query.status]} />
      </Stack>

      <Box sx={{ maxHeight: "18rem", overflowY: "auto", border: `1px solid ${tokens.border}`, borderRadius: "10px", p: "0.75rem", mb: "1rem" }}>
        {(query.messages || []).length === 0 ? (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>No messages.</Box>
        ) : query.messages.map((m, i) => {
          const mine = m.authorRole === "admin";
          return (
            <Box key={i} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: "0.75rem" }}>
              <Box sx={{ maxWidth: "80%", background: mine ? `${tokens.accentTint}` : tokens.surfaceSubtle, borderRadius: "10px", p: "0.5rem 0.75rem" }}>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.25rem" }}>
                  {m.authorRole === "admin" ? "HR" : "Employee"}{m.author ? ` · ${m.author.fname || ""}` : ""} · {fmt(m.createdAt)}
                </Box>
                <Box sx={{ fontSize: "0.875rem", color: tokens.text }}>{m.body}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {query.resolution && (
        <InlineMessage tone="success" sx={{ mb: "1rem" }}>
            <b>Resolution:</b> {query.resolution}
          </InlineMessage>
      )}

      {!closed && (
        <>
          <FormField type="text" value={reply} onChange={(e) => setReply(e.target.value)} fullwidth placeholder="Write a reply…" />
          {isAdmin && (
            <Box sx={{ mt: "0.75rem" }}>
              <FormField type="text" value={resolution} onChange={(e) => setResolution(e.target.value)} fullwidth placeholder="Resolution note (on resolve/reject)" />
            </Box>
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: "1.25rem" }}>
            <Box>
              {isAdmin && (
                <Stack direction="row" spacing={1}>
                  <BtnSecondary onClick={() => close("rejected")} disabled={busy} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder }}>Reject</BtnSecondary>
                  <BtnSecondary onClick={() => close("resolved")} disabled={busy} sx={{ borderRadius: "8px", color: tokens.successText, borderColor: tokens.success }}>Resolve</BtnSecondary>
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <BtnSecondary onClick={onClose} disabled={busy} sx={{ borderRadius: "8px" }}>Close</BtnSecondary>
              <BtnPrimary onClick={send} disabled={busy || !reply.trim()} sx={{ borderRadius: "8px" }}>Reply</BtnPrimary>
            </Stack>
          </Stack>
        </>
      )}
      {closed && (
        <Stack direction="row" justifyContent="flex-end"><BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Close</BtnSecondary></Stack>
      )}
    </Dialog>
  );
};

export default QueryThread;
